import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { PipelineMetricsSummary } from '@/types/domain/pipeline';
import { Activity, Clock, AlertTriangle, ShieldCheck, CreditCard } from 'lucide-react';

interface PipelineMetricsHeaderProps {
  metrics: PipelineMetricsSummary;
  onFilterClick?: (filterType: string) => void;
  activeFilter?: string;
}

const formatInrCompact = (amount: number) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} Lakh`;
  }
  return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export const PipelineMetricsHeader: React.FC<PipelineMetricsHeaderProps> = ({
  metrics,
  onFilterClick,
  activeFilter,
}) => {
  const cards = [
    {
      key: 'ACTIVE',
      title: 'Active Transactions',
      value: metrics.activeTransactions.toString(),
      subtitle: 'In-flight lifecycle',
      icon: Activity,
      colorClass: 'text-primary bg-primary/10 border-primary/20',
      badge: 'Live',
    },
    {
      key: 'VALUE',
      title: 'Total Pipeline Value',
      value: formatInrCompact(metrics.totalPipelineValue),
      subtitle: 'Across all 12 stages',
      iconText: '₹',
      colorClass: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      badge: 'Commitment',
    },
    {
      key: 'DELAYED',
      title: 'Delayed Deliveries',
      value: metrics.delayedOrders.toString(),
      subtitle: 'PO milestone breached',
      icon: Clock,
      colorClass: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
      badge: 'Delayed',
    },
    {
      key: 'SAP_EXCEPTION',
      title: 'SAP Sync Exceptions',
      value: metrics.sapSyncExceptions.toString(),
      subtitle: 'Validation & ERP alerts',
      icon: AlertTriangle,
      colorClass: 'text-rose-600 bg-rose-500/10 border-rose-500/20',
      badge: 'Blocked',
    },
    {
      key: 'OVERDUE_APPROVAL',
      title: 'Overdue Approvals',
      value: metrics.overdueApprovals.toString(),
      subtitle: 'SLA threshold exceeded',
      icon: ShieldCheck,
      colorClass: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
      badge: 'Escalated',
    },
    {
      key: 'PAYMENT',
      title: 'Payment Processing',
      value: metrics.paymentProcessing.toString(),
      subtitle: 'Pending treasury dispatch',
      icon: CreditCard,
      colorClass: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
      badge: 'Treasury',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {cards.map((card) => {
        const Icon = card.icon;
        const isActive = activeFilter === card.key;

        return (
          <Card
            key={card.key}
            onClick={() => onFilterClick && onFilterClick(card.key)}
            className={`transition-all duration-200 cursor-pointer rounded-2xl border ${
              isActive
                ? 'border-primary ring-2 ring-primary/20 shadow-md scale-[1.02] bg-card'
                : 'border-border hover:border-primary/50 hover:shadow-xs bg-card'
            }`}
          >
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className={`p-2 rounded-xl border flex items-center justify-center font-bold text-sm ${card.colorClass}`}>
                  {Icon ? <Icon className="h-4 w-4" /> : <span className="text-base font-extrabold font-mono">{card.iconText}</span>}
                </div>
                <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-muted text-foreground-secondary font-poppins">
                  {card.badge}
                </span>
              </div>

              <div>
                <span className="font-poppins text-xl sm:text-2xl font-extrabold tracking-tight text-foreground block truncate">
                  {card.value}
                </span>
                <span className="text-xs font-bold text-foreground leading-tight block truncate font-poppins">
                  {card.title}
                </span>
              </div>

              <p className="text-[10px] text-foreground-muted font-medium truncate pt-1 border-t border-border/40 font-poppins">
                {card.subtitle}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
